import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import InventoryAPI from "../../services/inventoryService";
import serviceItemsService from "../../services/serviceItemsService";
import { getServiceItemTemplates } from "../../services/servicesApiV2";

/**
 * ServiceItemsModal
 *
 * Primary UI component for assigning products to service items on jobs.
 * Opened from the cube button on every service row.
 *
 * Each row: Label | Brand (dropdown) | Product (dropdown) | Delete
 *
 * On save: diffs against server state (POST new, PATCH changed, DELETE removed).
 */
const ServiceItemsModal = ({
  show,
  onHide,
  service,
  installerId,
  onItemsChanged,
  readOnly = false
}) => {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [productsByBrand, setProductsByBrand] = useState({});
  const [stockLevels, setStockLevels] = useState({});
  const [unitLabel, setUnitLabel] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [originalItems, setOriginalItems] = useState([]);

  const requestServiceId = service?.id || service?.request_service_id;

  useEffect(() => {
    let cancelled = false;

    if (show && service) {
      loadData(cancelled);
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, service]);

  const loadData = async (cancelled) => {
    setLoading(true);
    setError(null);
    try {
      // Debug: log service object to see available fields
      console.log('[ServiceItemsModal] service object:', service);
      console.log('[ServiceItemsModal] requestServiceId:', requestServiceId);

      // Load job-level items (if requestServiceId exists)
      const serverItems = requestServiceId
        ? await serviceItemsService.getItems(requestServiceId)
        : [];

      if (cancelled) return;

      console.log('[ServiceItemsModal] serverItems:', serverItems);

      // Filter to material items only
      let materialItems = (serverItems || []).filter(i => !i.type || i.type === 'material');

      // If no job-level items exist, load template items from master service
      // master_service_id is the primary key in installer_services table where templates are stored
      const masterServiceId = service.master_service_id || service.service_id || service.installer_service_id;
      console.log('[ServiceItemsModal] masterServiceId for template lookup:', masterServiceId);
      if (materialItems.length === 0 && masterServiceId) {
        try {
          const templateItems = await getServiceItemTemplates(masterServiceId);
          console.log('[ServiceItemsModal] templateItems:', templateItems);
          if (cancelled) return;
          const templateMaterialItems = (templateItems || []).filter(i => !i.type || i.type === 'material');
          // Convert template items to editable rows (without id, so they'll be created on save)
          materialItems = templateMaterialItems.map(t => ({
            ...t,
            id: null, // Mark as new — will be created on save
            _fromTemplate: true,
            service_task_id: t.id // Keep reference to template
          }));
        } catch (templateErr) {
          console.log('No template items found or error loading:', templateErr);
        }
      }

      if (cancelled) return;

      // Load products with stock levels + allocated quantities + category config
      const [stockData, allocatedData, categories] = await Promise.all([
        InventoryAPI.getStockLevels(installerId),
        InventoryAPI.getAllocatedInventory(installerId).catch(() => []),
        InventoryAPI.getServiceCategories().catch(() => [])
      ]);

      if (cancelled) return;

      // Build stock lookup: { productId: { onHand, allocated } }
      const stockMap = {};
      (stockData || []).forEach(s => {
        const pid = s.product_id || s.id;
        stockMap[pid] = { onHand: parseFloat(s.quantity_on_hand) || 0, allocated: 0 };
      });
      (allocatedData || []).forEach(a => {
        if (stockMap[a.product_id]) {
          stockMap[a.product_id].allocated = parseFloat(a.allocated_quantity) || 0;
        } else {
          stockMap[a.product_id] = { onHand: 0, allocated: parseFloat(a.allocated_quantity) || 0 };
        }
      });
      setStockLevels(stockMap);

      // Determine unit label from category config
      const serviceCat = service.service_category || '';
      const catConfig = (categories || []).find(c =>
        (c.name || c.category_name || '').toLowerCase() === serviceCat.toLowerCase()
      );
      setUnitLabel(catConfig?.default_unit_code || '');

      // Use stock data as product source (includes product metadata)
      const allProducts = stockData || [];

      // Filter products by category
      const categoryProducts = (allProducts || []).filter(p =>
        p.category_name === service.service_category
      );
      const sorted = categoryProducts.sort((a, b) => {
        const brandA = (a.brand || '').toLowerCase();
        const brandB = (b.brand || '').toLowerCase();
        if (brandA !== brandB) return brandA.localeCompare(brandB);
        const nameA = (a.product_name || a.name || '').toLowerCase();
        const nameB = (b.product_name || b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });

      const uniqueBrands = [...new Set(sorted.map(p => p.brand).filter(Boolean))].sort();
      const byBrand = {};
      sorted.forEach(p => {
        const brand = p.brand || '';
        if (!byBrand[brand]) byBrand[brand] = [];
        byBrand[brand].push(p);
      });

      if (cancelled) return;

      setProducts(sorted);
      setBrands(uniqueBrands);
      setProductsByBrand(byBrand);

      // Map items to editable rows
      const rows = materialItems.map((item, idx) => ({
        _key: item.id || `template-${idx}-${Date.now()}`,
        id: item.id || null,
        serviceTaskId: item.service_task_id,
        label: item.label || '',
        brand: item.recommended_product_brand || '',
        productId: item.recommended_product_id || null,
        productName: item.recommended_product_name || '',
      }));

      // If still no items, show one empty row pre-populated with the service name
      if (rows.length === 0 && !readOnly) {
        const defaultLabel = service.description || service.custom_description || service.service_name || service.service_category || '';
        rows.push({
          _key: `new-${Date.now()}`,
          id: null,
          serviceTaskId: null,
          label: defaultLabel,
          brand: '',
          productId: null,
          productName: ''
        });
      }

      setItems(rows);
      setOriginalItems(JSON.parse(JSON.stringify(rows.filter(r => r.id))));
    } catch (err) {
      console.error('Error loading service items:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const addRow = () => {
    setItems(prev => [...prev, {
      _key: `new-${Date.now()}`,
      id: null,
      serviceTaskId: null,
      label: '',
      brand: '',
      productId: null,
      productName: ''
    }]);
  };

  const removeRow = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
    setValidationErrors(prev => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const updateRow = (index, field, value) => {
    setItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      if (field === 'brand') {
        updated[index].productId = null;
        updated[index].productName = '';
      }

      if (field === 'productId') {
        const productId = value ? parseInt(value, 10) : null;
        updated[index].productId = productId;
        if (productId) {
          const product = products.find(p => (p.product_id || p.id) === productId);
          updated[index].productName = product ? (product.product_name || product.name) : '';
          // Auto-fill brand if not set
          if (!updated[index].brand && product?.brand) {
            updated[index].brand = product.brand;
          }
        } else {
          updated[index].productName = '';
        }
      }

      return updated;
    });

    if (validationErrors[index]) {
      setValidationErrors(prev => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
    }
  };

  const validate = () => {
    const errors = {};
    const labels = {};

    // Filter out completely empty rows (no label, no product) - don't validate them, just skip
    const nonEmptyItems = items.filter(item => item.label.trim() || item.productId);

    nonEmptyItems.forEach((item) => {
      const origIndex = items.indexOf(item);
      if (!item.label.trim()) {
        errors[origIndex] = 'Label is required';
        return;
      }
      const lower = item.label.trim().toLowerCase();
      if (labels[lower] !== undefined) {
        errors[origIndex] = 'Duplicate label';
        errors[labels[lower]] = 'Duplicate label';
      }
      labels[lower] = origIndex;
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    if (!requestServiceId) {
      setError('Cannot save items — service has no row ID');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      // Filter out completely empty rows
      const itemsToSave = items.filter(item => item.label.trim() || item.productId);

      const originalMap = {};
      originalItems.forEach(o => { if (o.id) originalMap[o.id] = o; });

      const currentIds = new Set(itemsToSave.filter(i => i.id).map(i => i.id));

      // Delete removed items
      for (const orig of originalItems) {
        if (orig.id && !currentIds.has(orig.id)) {
          await serviceItemsService.deleteItem(orig.id);
        }
      }

      // Create new items, update changed items
      for (const item of itemsToSave) {
        if (!item.id) {
          // New item
          if (item.label.trim()) {
            await serviceItemsService.createItem(requestServiceId, {
              label: item.label.trim(),
              type: 'material',
              recommended_product_id: item.productId || null,
              recommended_product_name: item.productName || null,
              recommended_product_brand: item.brand || null
            });
          }
        } else {
          // Existing item — check if changed
          const orig = originalMap[item.id];
          if (orig && (
            orig.label !== item.label.trim() ||
            orig.brand !== item.brand ||
            orig.productId !== item.productId
          )) {
            await serviceItemsService.updateItem(item.id, {
              label: item.label.trim(),
              recommended_product_id: item.productId || null,
              recommended_product_name: item.productName || null,
              recommended_product_brand: item.brand || null
            });
          }
        }
      }

      if (onItemsChanged) onItemsChanged();
      onHide();
    } catch (err) {
      console.error('Error saving service items:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    onHide();
  };

  // Count material items with products assigned (for status display)
  const materialItems = items.filter(i => i.label.trim());
  const assignedCount = materialItems.filter(i => i.productId).length;
  const totalCount = materialItems.length;

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa-sharp fa-regular fa-cube mr-2"></i>
          Preferred Products
          {service && (
            <small className="text-muted d-block" style={{ fontSize: '0.65em' }}>
              {service.service_category ? `${service.service_category} - ` : ''}{service.description || service.custom_description || service.service_name}
            </small>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" className="mr-2" />
            Loading items...
          </div>
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : (
          <>
            {totalCount > 0 && (
              <div className="mb-3">
                <small className={assignedCount === totalCount && totalCount > 0 ? 'text-success' : 'text-muted'}>
                  <i className={`fa-sharp fa-regular ${assignedCount === totalCount && totalCount > 0 ? 'fa-check-circle' : 'fa-info-circle'} mr-1`}></i>
                  {assignedCount} of {totalCount} item{totalCount !== 1 ? 's' : ''} have products assigned
                </small>
              </div>
            )}

            {products.length === 0 && !readOnly && (
              <div className="alert alert-warning mb-3">
                <i className="fa-sharp fa-regular fa-triangle-exclamation mr-2"></i>
                <strong>No products defined</strong> for the <em>{service?.service_category || 'this'}</em> category.
                Add products in <strong>Inventory</strong> to enable product assignment.
              </div>
            )}

            {items.length === 0 && readOnly ? (
              <div className="text-center text-muted py-3">
                No items defined for this service.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm mb-0">
                  <thead className="thead-light">
                    <tr>
                      <th style={{ width: '28%' }}>Label</th>
                      <th style={{ width: '25%' }}>Brand</th>
                      <th style={{ width: '35%' }}>Product</th>
                      {!readOnly && <th style={{ width: '12%' }}></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={item._key}>
                        <td>
                          <Form.Control
                            size="sm"
                            type="text"
                            value={item.label}
                            onChange={(e) => updateRow(index, 'label', e.target.value)}
                            placeholder="e.g., Windshield"
                            readOnly={readOnly || !!item.serviceTaskId}
                            isInvalid={!!validationErrors[index]}
                          />
                          {validationErrors[index] && (
                            <Form.Text className="text-danger">{validationErrors[index]}</Form.Text>
                          )}
                        </td>
                        <td>
                          <Form.Control
                            as="select"
                            size="sm"
                            value={item.brand}
                            onChange={(e) => updateRow(index, 'brand', e.target.value)}
                            disabled={readOnly}
                          >
                            <option value="">--</option>
                            {brands.map(b => (
                              <option key={b} value={b}>{b}</option>
                            ))}
                          </Form.Control>
                        </td>
                        <td>
                          <Form.Control
                            as="select"
                            size="sm"
                            value={item.productId || ''}
                            onChange={(e) => updateRow(index, 'productId', e.target.value)}
                            disabled={readOnly || !item.brand}
                            className={!readOnly && item.label.trim() && !item.productId ? 'border-warning' : ''}
                          >
                            <option value="">--</option>
                            {(productsByBrand[item.brand] || []).map(p => {
                              const pid = p.product_id || p.id;
                              const pname = p.product_name || p.name;
                              const stock = stockLevels[pid];
                              const unit = unitLabel ? ` ${unitLabel}` : '';
                              const stockText = stock
                                ? ` (${stock.onHand}${unit} in stock, ${stock.allocated} allocated)`
                                : '';
                              return (
                                <option key={pid} value={pid}>{pname}{stockText}</option>
                              );
                            })}
                          </Form.Control>
                        </td>
                        {!readOnly && (
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-link text-danger p-0"
                              onClick={() => removeRow(index)}
                              title="Remove"
                            >
                              <i className="fa-sharp fa-regular fa-trash"></i>
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!readOnly && (
              <button
                type="button"
                className="btn btn-sm btn-outline-primary mt-2"
                onClick={addRow}
              >
                <i className="fa-sharp fa-regular fa-plus mr-1"></i> Add Item
              </button>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {readOnly ? (
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={handleClose} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={loading || saving}>
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="mr-1" />
                  Saving...
                </>
              ) : 'Save'}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ServiceItemsModal;
